import { computed, reactive, ref } from 'vue'
import type {
  AthleteIdString,
  DateIso,
  DateIsoIsh,
  DateTimeIso,
  DateTimeUTC,
  EntryIdString,
  EventGroupId,
  EventGroupIdString,
  EventId,
  EventIdString
} from '@/services/common'

import * as ResultsService from '@/components/results/results-service'
import * as CommonServiceUtils from '@/services/common-service-utils'
import { useEventListener, useVibrate, useWakeLock, useWebSocket } from '@vueuse/core'
import { SocketMessageDeduplicationService } from '@/components/results/socket/socket-message-deduplication-service'
import type {
  ResultSocketActionType,
  SocketListenConfig,
  SocketResultData
} from '@/components/results/socket/result-socket-models'
import type {
  AO_CODE,
  EventType,
  EventTypeMap,
  IBaseConcrete,
  IsoDate
} from '@/common/common-models'
import * as SocketService from '@/components/results/socket/socket-service'
import type {
  FieldResults_AthleteResult,
  SocketResultMessagePayloadFieldResults
} from '@/components/results/socket/socket-field-results/socket-field-results-models'
import * as SocketFieldResultsService from '@/components/results/socket/socket-field-results/socket-field-results-service'
import type { SocketResultMessagePayloadResultsManual } from '@/components/results/socket/socket-results-manual/socket-results-manual-models'
import * as SocketResultsManualService from '@/components/results/socket/socket-results-manual/socket-results-manual-service'
import type {
  LiveFeedMessageBase,
  LiveFeedMessageBaseGeneric
} from '@/components/results/socket/live-feed/live-feed-models'
import * as LiveFeedService from '@/components/results/socket/live-feed/live-feed-service'
import { io } from 'socket.io-client'

import type { SocketsResultsPfPayload } from '@/components/results/socket/socket-results-pf/sockets-results-pf-models'
import * as SocketsResultsPfService from '@/components/results/socket/socket-results-pf/sockets-results-pf-service'
import { convertExternalJsonToCompetitionOnTheDay } from '@/components/results/middleware/competition-converter.service'
import type { IndividualResultsResponse } from '@/components/results/individual-results/individual-results-service'
import type {
  ApiCompetitionAthlete,
  ApiCompetitionCachedDataSummary,
  ApiCompetitionEntry,
  ApiCompetitionOnTheDay,
  ApiCompetitionOptions,
  ApiCompetitionSchedule,
  ApiCompetitionScheduleOptions,
  ApiResultHeader,
  ApiServerResponse
} from '@/components/results/middleware/competition-converter.models'
import type { SocketResultMessagePayloadAthleteBibNo } from '@/components/results/socket/models/athlete-bibno'
import type { SocketResultMessagePayloadFieldNextup } from '@/components/results/socket/models/field-nextup'
import { RESULT_SOCKET_ACTION_TYPES } from '@/components/results/socket/result-socket-models'
// import { factoryLiveFeedMessageField } from '@/components/results/socket/live-feed/live-feed-service'
// import { createGuid } from '@/services/common-service-utils'
import type { LiveFeedSocketMessage } from '@/components/results/socket/live-feed/models/live-feed-models'
import type { SocketResultMessagePayloadSeedDropped } from '@/components/results/socket/seed-dropped/models/seed-dropped'
import { updateSeedDropped } from '@/components/results/socket/seed-dropped/models/seed-dropped-service'
import type { SocketResultMessagePayloadTrackMove } from '@/components/results/socket/track-move/models/track-move'
import type { SocketResultMessagePayloadTrackMoveVert } from '@/components/results/socket/track-movevert/models/track-movevert'
import { updateTrackMove } from '@/components/results/socket/track-move/models/track-move-service'
import { updateTrackMoveVert } from '@/components/results/socket/track-movevert/models/track-movevert-service'
import type { SocketResultMessagePayloadEntriesPresent } from '@/components/results/socket/entries-present/models/entries-present'
import { updateEntriesPresent } from '@/components/results/socket/entries-present/models/entries-present-service'
import type { SocketResultMessagePayloadSeedConfirmed } from '@/components/results/socket/seed-confirmed/models'
import { updateSeedConfirmed } from '@/components/results/socket/seed-confirmed/models/seed-confirmed-service'
import type { SocketResultMessagePayloadEntriesCheckin } from '@/components/results/socket/entries-checkin/models'
import { updateEntriesCheckIn } from '@/components/results/socket/entries-checkin/models/entries-checkin-service'
import type { SocketResultMessagePayloadQualifyResults } from '@/components/results/socket/qualify-results/models/qualify-results'
import { applyQualifyResults } from '@/components/results/socket/qualify-results/models/qualify-results-service'
import { isUtcTime, simpleClone } from '@/services/common-service-utils'
import {
  doesCompEventHaveTrials,
  eventQualificationTrialsDescription,
  eventQualifyDescription
} from '@/components/results/results-service'
import type { SocketResultMessagePayloadTrackMoveHorz } from '@/components/results/socket/track-movehorz/models/track-movehorz'
import { updateTrackMoveHorz } from '@/components/results/socket/track-movehorz/models/track-movehorz-service'

// import { serverResponse697 } from '@/components/results/mocks/comps/comp697'
// import { mockLiveFeedF26Hammer } from '@/components/results/socket/live-feed/mock-live-feed-f26-hammer'
// import { mockFieldResultsCardHammerPayload } from '@/components/results/socket/socket-field-results/mock-hammer-field-results'
// import { mockF26HammerPayload } from '@/components/results/socket/socket-field-results/mockF26Hammer'
// import { mockPhotoFinishT1Payload } from '@/components/results/socket/socket-results-pf/mock/mockPhotoFinishT1'
// import { mockF26HammerMatthewPayload } from '@/components/results/socket/socket-field-results/mockF26HammerMatthew'
// import { mockPhotoFinishT1Payload } from './socket/socket-results-pf/mock/mockPhotoFinishT1'

export type EntryGender = 'M' | 'F'
export type TrackTypeNo = `T${number}`
export type FieldTypeNo = `F${number}`
// export type ResultPositionString = string
export type HeatNoString = string
export type ScoreNeedsCleaning = string
export type TrialNo = 't1' | 't2' | 't3' | 't4' | 't5' | 't6' | 't7' | 't8'
export type TrialMap = Record<TrialNo, ScoreNeedsCleaning | number>
export type AthleteTrialMap = Partial<TrialMap>

// In athletics when they're doing heights or long jumps the symbols they used to indicate success pass failure
export type AthleteTrialAbnormalType = 'x' | 'X' | 'o' | 'O' | '-'

export type CompetitionOptions = ApiCompetitionOptions

/*
below might be
{
  '1.75': 'O',
  '1.80': 'xxO',  <---
  '1.85': 'O',
  '1.90': 'O',
  '1.95': 'O',
  '2.00': 'X0',
  '2.05': 'O',
  '2.10': 'O'
}
 */
export type AthleteTrialMapAbnormal = Record<string, string>

export interface CompetitionOnTheDayServer {
  compData: CompetitionOnTheDay
  liveFeed: LiveFeedMessageServer[]
  socketId: number // When comp periodically saves the socketId indicates which messages have been processed
}

export type SocketPayloadAccepted =
  | SocketResultMessagePayloadResultsManual
  | SocketResultMessagePayloadFieldResults
  | SocketsResultsPfPayload
  | SocketResultMessagePayloadAthleteBibNo
  | SocketResultMessagePayloadFieldNextup

export interface LiveFeedMessageServer {
  id: number
  created: DateTimeIso
  action: ResultSocketActionType
  payload:
    | string
    | SocketResultMessagePayloadResultsManual
    | SocketResultMessagePayloadFieldResults
    | SocketsResultsPfPayload
}

export type CompTrialResults = Record<
  EventIdString | EventId,
  Record<AthleteIdString, AthleteTrialMap>
>

export type CompetitionCachedDataSummary = ApiCompetitionCachedDataSummary

export interface CompetitionOnTheDay {
  id: number
  name: string
  date: DateIso
  location: string
  organiser: string
  checkIn: boolean
  logo: string
  events: Record<EventGroupIdString, CompEvent>
  domain: string
  trialResults: CompTrialResults
  options: CompetitionOptions
  cachedDataSummary: CompetitionCachedDataSummary
}

export interface CompEventBase {
  egId: EventGroupId
  name: string
  eventDefName?: string
}

export type CompetitionScheduleOptions = ApiCompetitionScheduleOptions

export interface CompEvent extends CompEventBase {
  eventNo: number
  eventText?: string // E.g. an age group
  typeNo: TrackTypeNo | FieldTypeNo
  eventDate: DateTimeUTC
  egOptions: CompetitionScheduleOptions
  entries?: Record<AthleteIdString, IndividualEntry>
  results?: Record<HeatNoString, IndividualResult[]>
  teams?: Team[]
}

export interface ApiScheduleResponse {
  events: Record<EventGroupIdString, CompEvent>
}

export interface Pof10 {
  pbPerf: null | number | string
  pbAchieved: null | DateIsoIsh
  sbPerf: null | number | string
  sbAchieved: null | DateIsoIsh
}

export interface Seeding {
  heatNo: number
  laneNo: number
}

export interface IndividualEntry {
  entryId: number
  athleteId: number
  URN: string
  firstName: string
  surName: string
  aoCode: AO_CODE
  perf: string | number | null | undefined // '0.00'
  eventAgeGroup: string // 'Under 15'
  ageGroup: string // 'Under 15'
  bibNo: string | null
  teamBibNo: string
  clubName: string
  gender: EntryGender
  seeding: Seeding
  pof10?: Pof10
  present: 1 | 0 | null
  wind?: string | number
  collected: 1 | 0 // Collected their bib
  checkedIn: 1 | 0 // Checked in for that event
  heatNoCheckedIn: number
  laneNoCheckedIn: number
  classification: number
  periodStart?: DateTimeUTC
  isWaitlisted?: boolean
  waitlistPosition?: number | null
}

export interface IndividualResult {
  laneNo: number
  position: number
  wind: string
  athlete: string // 'John Smith'
  athleteId: number //  Might not match an entry...as they were a reserve
  affilation: string // 'Unattached',
  bibNo: string //  '1',
  score: string //  '9.80',
  scoreText?: string
  qualify: string | null //  '' ?
  animateThisAthlete?: boolean
  fieldResultAthlete?: FieldResults_AthleteResult
  rhOptions?: {
    description: string
  }
  ageGroup: string
}

export interface IndividualSeriesScheduledItem {
  egId: number
  name: string
  eventId: number
  eventName: string // e.g. High Jump Pool B (Bed 2)
  eventDefName: string // e.g. High Jump
}

export interface StoreCompResultsState {
  isReady: boolean
  doneInitialLoad: boolean
  isLoading: boolean
  loadingMessage: string
  error: string | null
  apiServerResponse: ApiServerResponse | null
  competitionOnTheDay: CompetitionOnTheDay
  longPollDateTime: DateTimeIso
  env: string
  liveFeedLoad: {
    receivedOnLoad: LiveFeedMessageServer[]
    processed: LiveFeedMessageServer[]
  }
  dates: DateIso[]
  scheduledEvents: CompEvent[]
  hasTeamResults: boolean
  hasIndivSeries: boolean
  indivSeriesScheduledItems: IndividualSeriesScheduledItem[]
  indivResultsData: Map<number, IndividualResultsResponse>
  indivResultsSelectedEventGroupId: number | null
  indivResultsLoading: boolean
  indivResultsError: string | null
  schedule: {
    sortBy: keyof CompEvent
    filterTerm: string
    filterDateNextSequence: IsoDate //  The next date in the sequence, will keep this constant.
    filterDate: IsoDate
    filterEventType: EventType | 'ALL'
    filterIsEnabled: {
      any: boolean
      date: boolean
      eventType: boolean
      term: boolean
      count: number
    }
    eventTypesAvailable: Partial<EventTypeMap>
  }
  selectedEvent: {
    compEvent: CompEvent
    compEventFiltered: CompEvent //  For filtering entries, a copy of the original "compEvent"
    searchTerm: string //  For filtering entries
    eventDateDisplay: string
    eventTimeDisplay: string
    eventAgeGroup: string
    hasEntries: boolean
    hasResults: boolean
    isTrack: boolean
    isTeam: boolean
    qualify: {
      forward: {
        egId: number
        name: string
      }
      backward: {
        egId: number
        name: string
      }
    }
  }
  entries: IndividualEntry[]
  maps: {
    eventsByEventGroupId: Record<EventGroupIdString, ApiCompetitionSchedule[]>
    entriesByEntryId: Record<EntryIdString, EventGroupIdString>
    resultsHeadersOptionsByEventGroupIdAndHeatNo: Record<EventGroupIdString, ApiResultHeader>
    socketsByKey: Record<string, SocketResultData<unknown>>
    socketsByContentHash: Record<string, SocketResultData<unknown>>
    qualifyLinks: QualifyLinks
  }
  athletes: Record<AthleteIdString, ApiCompetitionAthlete>
  ui: {
    showSection:
      | 'SCHEDULE'
      | 'EVENT_DETAIL'
      | 'LIVE_FEED'
      | 'TEAM_RESULTS'
      | 'INDIVIDUAL_RESULTS'
      | 'ATHLETES'
      | 'FILTERS'
    eventDetail: {
      showTab: 'DETAIL_ENTRIES' | 'DETAIL_START_LIST' | 'DETAIL_RESULTS'
    }
    showFollowEventSetup: boolean
    showingFilters: boolean
  }
  text: {
    date: string
  }
  socket: {
    isConnected: boolean
    deviceKey: string
    compAtSocketId: number
    connectedAt: Date | null
    disconnectedAt: Date | null
    messagesToProcess: unknown[]
    liveFeed: LiveFeedMessageBase[]
    liveFeedSocketMessages: LiveFeedSocketMessage[]
    liveFeedQueueSize: number
    followingEvents: FollowingEvents
  }
}

export interface Team {
  athletes: TeamAthlete[]
  id: number
  teamName: string
  teamBibNo: string | number | null
  egId: number
  seeding?: Seeding | {}
  present: 1 | 0 | null
}

export interface TeamAthlete {
  id: number
  firstName: string
  surName: string
}

export type FollowingEvents = Record<EventIdString, IBaseConcrete>

export interface ShouldFollowEvent {
  isFollowing: boolean
  compEvent: CompEventBase
}

export type QualifyLink = {
  from: {
    egId: EventGroupIdString
    name: string
  } | null
  to: {
    egId: EventGroupIdString
    name: string
  } | null
}

export type QualifyLinks = Record<EventGroupIdString, QualifyLink>

const globalState = reactive<StoreCompResultsState>(ResultsService.factoryStoreCompResultsState())

// Global deduplication service instance
const socketDeduplicationService = new SocketMessageDeduplicationService(200)

// Global socket instance - singleton pattern
let globalSocket: any = null
let socketInitialized = false

/**
 * Initialize socket connection (singleton)
 */
function initializeSocket() {
  if (globalSocket && socketInitialized) {
    console.log('Socket already initialized, reusing existing connection')
    return globalSocket
  }

  const SOCKET_URL_AWS = import.meta.env.VITE_APP_SOCKET_URL

  console.log('Creating new socket connection to:', SOCKET_URL_AWS)

  globalSocket = io(SOCKET_URL_AWS, {
    reconnectionAttempts: 10,
    reconnectionDelay: 2000,
    autoConnect: true
  })

  socketInitialized = true
  return globalSocket
}

/**
 * Cleanup socket connection
 */
function cleanupSocket() {
  if (globalSocket) {
    console.log('Cleaning up socket connection')
    globalSocket.removeAllListeners()
    globalSocket.disconnect()
    globalSocket = null
    socketInitialized = false
  }
}

/**
 * Register page-unload listeners using native addEventListener.
 *
 * useEventListener from @vueuse/core ties listeners to the component
 * lifecycle — on SPA navigation the component unmounts first, removing
 * the listener before beforeunload ever fires, leaving orphaned
 * connections on the server.
 *
 * Native addEventListener at module scope registers once per page and
 * survives all navigations within the app.
 */
let pageUnloadListenersRegistered = false
function registerPageUnloadHandlers() {
  if (pageUnloadListenersRegistered) return
  pageUnloadListenersRegistered = true

  const handler = () => {
    console.log('Page unloading, cleaning up socket to prevent orphaned connections')
    cleanupSocket()
  }

  window.addEventListener('beforeunload', handler)
  window.addEventListener('unload', handler)
}

registerPageUnloadHandlers()

/**
 *
 */
export function useStoreCompResults() {
  const state = globalState

  const debugMessages = ref<string[]>([])

  const SOCKET_URL_AWS = import.meta.env.VITE_APP_SOCKET_URL
  const SOCKET_KEEP_ALIVE_PAYLOAD_DOMAIN = 'entry4sports.co.uk'
  const SOCKET_DOMAINS = [
    'dev.entry4sports.co.uk',
    'uat.entry4sports.co.uk',
    'https://entry4sports.co.uk',
    'https://entry4sports.co.uk',
    'https://lite.entry4sports.co.uk/'
  ]

  // Use singleton socket
  const socket = initializeSocket()

  // Set up event handlers only once
  if (!socket._e4sHandlersSetup) {
    console.log('Setting up socket event handlers')

    socket.on('connect', () => {
      console.log('Socket: connected', socket.id)
      state.socket.connectedAt = new Date()
      state.socket.isConnected = true
    })

    socket.on('disconnect', () => {
      console.log('Socket: disconnected')
      state.socket.disconnectedAt = new Date()
      state.socket.isConnected = false
    })

    socket.on('connect_error', () => {
      console.error('Socket: Failed to connect Socket.IO at: ' + new Date())
    })

    // Listen for subscription confirmations
    socket.on('subscribed', (data: { comp: { id: number } }) => {
      console.log(`Subscribed to competition: ${data.comp.id}`)
    })

    socket.on('unsubscribed', (data: { comp: { id: number } }) => {
      console.log(`Unsubscribed from competition: ${data.comp.id}`)
    })

    socket.on('message', (data: unknown) => {
      console.log('Socket: message received', data)
      // processSocketMessage(data)
    })

    //competition-update
    socket.on('competition-update', (data: MessageEvent) => {
      console.log('Socket: competition-update received', data)
      processSocketMessage(data)
    })

    /**
     * use this sparingly.  It's a big payload.
     */
    socket.on('cache-updated', (data: { payload: ApiCompetitionOnTheDay }) => {
      console.log('Socket: competition-update received', data)
      initSetupCompetition(data.payload)
    })

    // Add after the other socket.on handlers
    socket.onAny((event: string, ...args: unknown[]) => {
      console.log(`Socket.onAny: caught event "${event}"`, args)
      // You can add additional monitoring logic here
    })

    // Mark handlers as set up
    socket._e4sHandlersSetup = true
  }

  //  Wakelock can NOT be activated automatically, it must be activated by a user gesture.
  const wakeLock = reactive(useWakeLock())
  //wakeLock.request('screen')
  const wakeLockDebug = ref('')

  const isWakeLockOnText = computed(() => (wakeLock.isActive ? 'ON' : 'OFF'))

  function wakeLockCToggle() {
    return wakeLock.isActive ? wakeLock.release() : wakeLock.request('screen')
  }

  //  Vibrate on socket message but can't get it to work on PDA, think blocked.
  const vibrateController = useVibrate({ pattern: [300, 100, 300] })

  /**
   * Force a complete socket reconnection by disconnecting and reconnecting
   */
  function forceSocketReconnection() {
    console.log('Forcing socket reconnection...')

    // Disconnect the socket
    socket.disconnect()

    // Wait a brief moment then reconnect
    setTimeout(() => {
      console.log('Reconnecting socket...')
      socket.connect()

      // Re-join the socket room after reconnection
      if (state.competitionOnTheDay.id) {
        setTimeout(() => {
          joinSocketRoom(state.competitionOnTheDay.id)
        }, 1000) // Wait 1 second for connection to establish
      }
    }, 500) // Wait 500ms before reconnecting
  }

  /**
   * Check if socket connection is healthy by testing connectivity
   */
  function checkSocketHealth(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!socket.connected) {
        console.log('Socket health check: Not connected')
        resolve(false)
        return
      }

      // Send a ping and wait for response
      const timeout = setTimeout(() => {
        console.log('Socket health check: Timeout - no pong received')
        resolve(false)
      }, 5000) // 5 second timeout

      socket.emit('ping', { timestamp: Date.now() })

      socket.once('pong', () => {
        clearTimeout(timeout)
        console.log('Socket health check: Healthy - pong received')
        resolve(true)
      })
    })
  }

  /**
   * Smart reconnection that checks health first
   */
  async function smartSocketReconnection() {
    console.log('Checking socket health before reconnection...')

    const isHealthy = await checkSocketHealth()

    if (!isHealthy) {
      console.log('Socket unhealthy, forcing reconnection...')
      forceSocketReconnection()
    } else {
      console.log('Socket appears healthy, no reconnection needed')
    }
  }

  /**
   * Listen for visibility change, if the page is visible then get the competition data.  E.g. user
   * has switched tabs and come back.
   */
  useEventListener(document, 'visibilitychange', (evt) => {
    console.log('visibilitychange', evt)

    const isVisible = evt.target && (evt.target as HTMLDocument).visibilityState === 'visible'

    if (isVisible) {
      console.log('visibilitychange = visible')
      // debugMessages.value.push('visibilitychange = visible at: ' + new Date())
      console.log('visibilitychange = visible at: ' + new Date(), simpleClone(state))
      getCompetitionOnTheDay(state.competitionOnTheDay.id, false, state.env)

      // Check socket health and reconnect if needed when page becomes visible
      // This helps ensure the socket is properly connected after tab switching
      smartSocketReconnection()
    } else {
      console.log('visibilitychange = hidden at: ' + new Date())
    }
  })

  /**
   * Listen for clicks on the document, if the user clicks then check if the wakelock is active.
   * This bypasses the block for auto activate the wakelock.
   */
  useEventListener(document, 'click', (evt) => {
    console.log('document click', evt)
    checkWakeLock()
  })

  /**
   *
   * @param compId
   * @param env
   */
  function init(compId: number, env: string = 'EA') {
    const savedSelectedEventGroupId = state.indivResultsSelectedEventGroupId
    reset()
    state.indivResultsSelectedEventGroupId = savedSelectedEventGroupId
    state.env = env
    state.isLoading = true
    getCompetitionOnTheDay(compId, true, env).finally(() => {
      state.isLoading = false
      state.doneInitialLoad = true
    })
    joinSocketRoom(compId)
  }

  let pointerForSocketKeepAlive: number | null = null
  let pointerForCompetitionRefresh: number | null = null

  /**
   * Join the socket room for the competition.
   * @param compId
   */
  function joinSocketRoom(compId: number) {
    const message = {
      key: 0,
      comp: { id: compId },
      action: 'subscribe',
      deviceKey: state.socket.deviceKey,
      securityKey: '',
      domain: window.location.hostname,
      payload: { compId: compId }
    }

    socket.emit('message', JSON.stringify(message))
  }

  /**
   *
   */
  function reset() {
    Object.assign(state, ResultsService.factoryStoreCompResultsState())
  }

  /**
   *
   * @param id
   * @param initiateTimeout
   * @param env
   */
  function getCompetitionOnTheDay(
    id: number,
    initiateTimeout: boolean,
    env: string = 'EA'
  ): Promise<void> {
    // if comp id zero or less return
    if (id <= 0) {
      console.warn('getCompetitionOnTheDay() EXITING compId is zero or less: ' + id)
      return Promise.resolve()
    }

    const url =
      (import.meta.env.VITE_APP_API_HOST || 'https://live.entry4sports.com') +
      '/api/competitions/' +
      id +
      '/on-the-day?system=' +
      env

    // state.isLoading = true
    state.loadingMessage = 'Getting competition data...'

    const token = localStorage.getItem('auth_token')
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    return fetch(url, {
      method: 'GET',
      headers
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }
        return response.json()
      })
      .then((data: ApiServerResponse) => {
        console.log('competitionOnTheDay response', data)

        initSetupCompetition(data.data)

        state.isLoading = false

        //
        if (initiateTimeout && data.data.competitionClientConfig.refreshIntervalMs) {
          const refreshIntervalMs = data.data.competitionClientConfig.refreshIntervalMs
          console.log('getCompetitionOnTheDay refreshIntervalMs: ' + refreshIntervalMs)
          scheduleCompetitionRefresh(id, refreshIntervalMs)
        }
      })
      .catch((error) => {
        console.error('Error fetching competition data:', error)
        state.error = error
        // state.isLoading = false
      })
  }

  /**
   *
   * @param id
   * @param refreshIntervalMs
   */
  function scheduleCompetitionRefresh(id: number, refreshIntervalMs: number) {
    console.log('scheduleCompetitionRefresh()...' + new Date())
    // Always clear any existing timeout first
    if (pointerForCompetitionRefresh !== null) {
      console.log('scheduleCompetitionRefresh() Clearing existing competition refresh timeout')
      window.clearTimeout(pointerForCompetitionRefresh)
      pointerForCompetitionRefresh = null
    }

    if (state.isLoading) {
      console.log('scheduleCompetitionRefresh()...competition is loading, exiting')
      return
    }

    const MINIMUM_REFRESH_INTERVAL_MS = 5000

    // TODO For testing
    // refreshIntervalMs = 5000

    if (refreshIntervalMs < MINIMUM_REFRESH_INTERVAL_MS) {
      refreshIntervalMs = MINIMUM_REFRESH_INTERVAL_MS
    }

    // Set the new timeout
    pointerForCompetitionRefresh = window.setTimeout(() => {
      getCompetitionOnTheDay(id, true, state.env)
      // Set to null after execution to indicate no active timeout
      // pointerForCompetitionRefresh = null
    }, refreshIntervalMs)
  }

  /**
   *
   * @param apiCompetitionOnTheDay
   */
  function initSetupCompetition(apiCompetitionOnTheDay: ApiCompetitionOnTheDay) {
    console.log('init()')
    state.apiServerResponse = { data: apiCompetitionOnTheDay }
    state.longPollDateTime = new Date().toISOString()

    // loop accross apiServerResponse.data.athletes and add to state.athletes
    apiCompetitionOnTheDay.athletes.forEach((athlete: ApiCompetitionAthlete) => {
      state.athletes[athlete.id] = athlete
    })

    // loop across apiCompetitionOnTheDay.resultHeaders and if options is a string convert to json
    apiCompetitionOnTheDay.resultHeaders.forEach((header: ApiResultHeader) => {
      const optionsStr = header.options as unknown as string
      if (typeof optionsStr === 'string' && optionsStr.trim() !== '') {
        try {
          header.options = JSON.parse(optionsStr)
        } catch (e) {
          console.error('Error parsing header.options JSON for header.egid: ' + header.egid, e)
          header.options = { description: '' }
        }
      }

      // console.log('header: ' + header.egid + '-' + header.heatno)
      // console.log('header.options: ', header.options)

      // create a key of header.egid and heatno an stick it in state.maps.resultHeadersByEgIdAndHeatNo[egid-heatno] = header.options
      state.maps.resultsHeadersOptionsByEventGroupIdAndHeatNo[header.egid + '-' + header.heatno] =
        header
    })

    // const schedules = apiCompetitionOnTheDay.schedules

    // Loop across the schedule setting maps events by Event Group ID
    // This is used as a quick look up object
    apiCompetitionOnTheDay.schedules.forEach((schedule: ApiCompetitionSchedule) => {
      // need to check the schedule options are Json, they sometimes come off a text and we have to convert them.
      if (typeof schedule.egOptions === 'string') {
        try {
          JSON.parse(schedule.egOptions)
        } catch (e) {
          console.error('Error parsing egOptions JSON for schedule.egId: ' + schedule.egId, e)
          schedule.egOptions = '{}'
        }
        // schedule.egOptions = JSON.parse(schedule.egOptions)
      }

      if (!state.maps.eventsByEventGroupId[schedule.egId]) {
        state.maps.eventsByEventGroupId[schedule.egId] = []
      }
      state.maps.eventsByEventGroupId[schedule.egId].push(schedule)
    })

    state.maps.qualifyLinks = ResultsService.createQualifyToFromMap(
      apiCompetitionOnTheDay.schedules
    )

    // Map entries by entry id to event group id.
    // This is used as a quick look up object
    apiCompetitionOnTheDay.entries.forEach((entry: ApiCompetitionEntry) => {
      state.maps.entriesByEntryId[entry.entryId.toString()] = entry.egId
        ? entry.egId.toString()
        : ''
    })

    const competitionOnTheDay: CompetitionOnTheDay =
      convertExternalJsonToCompetitionOnTheDay(apiCompetitionOnTheDay)
    const competitionOnTheDayServer: CompetitionOnTheDayServer = {
      compData: competitionOnTheDay,
      liveFeed: apiCompetitionOnTheDay.socketMessages,
      socketId: 0
    }

    competitionOnTheDayServer.compData

    setUpCompetionOnTheDay(competitionOnTheDayServer)
  }

  /**
   *
   * @param competitionOnTheDayServer
   */
  function setUpCompetionOnTheDay(competitionOnTheDayServer: CompetitionOnTheDayServer) {
    state.loadingMessage = 'Setting up competition...'

    state.competitionOnTheDay = competitionOnTheDayServer.compData
    state.liveFeedLoad.receivedOnLoad = competitionOnTheDayServer.liveFeed
    state.text.date = CommonServiceUtils.getFormattedDate(state.competitionOnTheDay.date)
    state.dates = CommonServiceUtils.unique(
      ResultsService.getCompetitionDates(competitionOnTheDayServer.compData)
    ).sort()

    if (!state.doneInitialLoad) {
      state.schedule.filterDateNextSequence = CommonServiceUtils.getNextDateInSequence(state.dates)
      state.schedule.filterDate = state.schedule.filterDateNextSequence
    }

    const eventTypesForSelectFilter = {
      ALL: 'All',
      ...ResultsService.getEventTypesFromSchedule(Object.values(state.competitionOnTheDay.events))
    }

    // console.warn('eventTypesForSelectFilter', eventTypesForSelectFilter)

    state.schedule.eventTypesAvailable = eventTypesForSelectFilter

    state.socket.compAtSocketId = competitionOnTheDayServer.socketId

    state.loadingMessage = 'Loading live feeds...'
    setUpLiveFeed()
    setScheduleToDisplay()

    // Compute hasTeamResults by checking if any scheduled event has team results enabled
    state.hasTeamResults = state.scheduledEvents.some(
      (event) => event.egOptions?.teamResults?.enabled === true
    )

    // Compute individual series config
    fetchIndivSeriesConfig()

    // if an event is currently selected, ensure latest results are shown
    if (state.selectedEvent.compEvent.egId > 0) {
      const latestCompEventFromServer =
        state.competitionOnTheDay.events[state.selectedEvent.compEvent.egId.toString()]

      processUpdateSelectedEvent(latestCompEventFromServer)
    }
    state.isReady = true
  }

  /**
   * Determine if there is an individual results series and populate
   * the scheduled items for the dropdown.
   * TODO: Fill in the actual config property name when known.
   */
  function fetchIndivSeriesConfig() {
    // Stub: return placeholder data until config property name is determined

    // loop across scheduled events and find any that have egoptions.results.seriesName and length > 0, if any then set hasIndivSeries to true and add to indivSeriesScheduledItems with egId and name
    const indivSeriesEvents = state.scheduledEvents.filter(
      (event) => (event.egOptions?.results?.seriesName?.length ?? 0) > 0
    )

    state.hasIndivSeries = indivSeriesEvents.length > 0
    state.indivSeriesScheduledItems = indivSeriesEvents.map((event) => ({
      egId: event.egId,
      name: event.name,
      eventId: event.eventNo,
      eventName: event.name + ' ' + (event.eventText ?? 'NA'),
      eventDefName: event.eventDefName || event.name
    }))
    state.indivResultsData = new Map()

    state.indivResultsLoading = false
    state.indivResultsError = null
  }

  /**
   * Check if the wakelock is active, if not then request it.
   * Debug there in attempt to get it to set automatically.
   */
  function checkWakeLock() {
    wakeLockDebug.value = 'A...'

    if (!wakeLock.isActive) {
      wakeLockDebug.value = 'A...release'
      wakeLock
        .request('screen')
        .then(() => {
          wakeLockDebug.value = 'B...then'
        })
        .catch((err) => {
          wakeLockDebug.value = 'B...catch' + err
        })
        .finally(() => {
          // wakeLockDebug.value = 'B...finally'
        })
    } else {
      wakeLockDebug.value = 'A...already active'
    }
  }

  function setEventTypeFilter(eventType: EventType | 'ALL') {
    state.schedule.filterEventType = eventType
    setScheduleToDisplay()
  }

  function setShowFilters(showingFilters: boolean) {
    state.ui.showingFilters = showingFilters
  }

  function filterScheduleDate(filterDate: IsoDate) {
    console.log('filterScheduleDate() filterDate: ' + filterDate)
    state.schedule.filterDate = filterDate
    setScheduleToDisplay()
  }

  function filterSchedule(filterTerm: string) {
    state.schedule.filterTerm = filterTerm
    setScheduleToDisplay()
  }

  function clearFilterSchedule() {
    state.schedule.filterTerm = ''
    setScheduleToDisplay()
  }

  /**
   *
   */
  function setScheduleToDisplay() {
    //  pass thru any filters.
    //console.log('setScheduleToDisplay()>>>>>>>>>>>>>>>>>>>>>>>>>>>>')
    const schedule = ResultsService.sortSchedule(
      CommonServiceUtils.simpleClone(Object.values(state.competitionOnTheDay.events)),
      state.schedule.sortBy
    )

    //  Are there any filters enabled?
    const isDateFilter = state.schedule.filterDate !== state.schedule.filterDateNextSequence
    const isEventTypeFilter = state.schedule.filterEventType !== 'ALL'
    const isTermFilter = state.schedule.filterTerm !== ''

    state.schedule.filterIsEnabled.date = isDateFilter
    state.schedule.filterIsEnabled.eventType = isEventTypeFilter
    state.schedule.filterIsEnabled.term = isTermFilter

    state.schedule.filterIsEnabled.any = isDateFilter || isEventTypeFilter || isTermFilter

    state.schedule.filterIsEnabled.count = [isDateFilter, isEventTypeFilter, isTermFilter].filter(
      (f) => f
    ).length

    state.scheduledEvents = ResultsService.filterSchedule(
      schedule,
      state.schedule.filterTerm,
      state.schedule.filterDate,
      state.schedule.filterEventType
    )

    // Diagnostic: log entry counts for each scheduled event
    // console.log('[setScheduleToDisplay] scheduledEvents count:', state.scheduledEvents.length)
    for (const event of state.scheduledEvents) {
      const entryCount = event.entries ? Object.keys(event.entries).length : 0
      const egOptionsRaw = event.egOptions as any
      const maxAthletes = typeof egOptionsRaw === 'object' ? egOptionsRaw?.maxathletes : undefined
      // console.log(
      //   `[setScheduleToDisplay] egId=${event.egId}, name=${event.name}, entries=${entryCount}, maxathletes=${maxAthletes}, egOptionsType=${typeof event.egOptions}`
      // )
    }
  }

  /**
   *
   */
  function setUpLiveFeed() {
    //  loop through all messages in state.liveFeedLoad.receivedOnLoad check if payload is string,
    //  see what action it is, check by id that it is not in the state.liveFeedLoad.processed array
    // if not then process it and add it to the state.liveFeedLoad.processed array
    //  then add it to the liveFeed array
    // sort liveFeedMessages by id, so oldest first (with lowest id)
    const liveFeedMessages = CommonServiceUtils.simpleClone(state.liveFeedLoad.receivedOnLoad).sort(
      (a, b) => {
        return a.id - b.id
      }
    )
    const processedMessages = CommonServiceUtils.simpleClone(state.liveFeedLoad.processed)

    console.log(
      'setUpLiveFeed() liveFeedMessages, process only above compAtSocketId: ' +
        state.socket.compAtSocketId
    )

    // const liveFeed = simpleClone(state.socket.liveFeed)
    for (const message of liveFeedMessages) {
      const payload:
        | SocketResultMessagePayloadResultsManual
        | SocketResultMessagePayloadFieldResults
        | SocketsResultsPfPayload =
        typeof message.payload === 'string' ? JSON.parse(message.payload) : message.payload
      if (!payload) {
        continue
      }

      const messageId = message.id
      // const isGreaterThanCompAtSocketId = messageId > state.socket.compAtSocketId
      // if (!isGreaterThanCompAtSocketId) {
      //   console.log(
      //     'setUpLiveFeed() message already processed by server: ' +
      //       messageId +
      //       ' is less than ' +
      //       state.socket.compAtSocketId
      //   )
      //   continue
      // }
      const found = processedMessages.find((processedMessage) => {
        return processedMessage.id === messageId
      })
      if (!found) {
        processedMessages.push(message)

        const isLoadingLiveFeed = true
        // TODO
        // processSocketPayload(message as any as SocketResultData<unknown>, isLoadingLiveFeed)
        processSocketPayload(message as any as SocketResultData<unknown>)
      }
    }
  }

  function testSocketMessage(whichOne: 'finley' | 'isabelle' | 'matthew' | 'matthew2' | 'pf1') {
    // const dataPayload = whichOne === '1' ? mockFieldResultsCardHammerPayload : mockF26HammerPayload
    /*
    const dataMap = {
      finley: mockLiveFeedF26Hammer,
      isabelle: mockFieldResultsCardHammerPayload,
      matthew: mockF26HammerPayload,
      matthew2: mockF26HammerMatthewPayload,
      pf1: mockPhotoFinishT1Payload
    }

    const dataPayload = dataMap[whichOne]

    if (whichOne === 'pf1') {
      socketProcessPhotoResults(dataPayload as SocketsResultsPfPayload)
    } else {
      socketProcessFieldResults(dataPayload as SocketResultMessagePayloadFieldResults)
    }

    const liveFeedMessageBase = LiveFeedService.factoryLiveFeedMessageField(
      whichOne === 'pf1' ? 'photofinish' : 'field-results',
      dataPayload
    )
    if (liveFeedMessageBase) {
      addToLiveFeed(liveFeedMessageBase)
    }
    */
  }

  /**
   *
   * @param messageEvent
   */
  function processSocketMessage(messageEvent: MessageEvent) {
    // let messageData
    // if ((messageEvent as MessageEvent).data) {
    const messageData = (messageEvent as MessageEvent).data || messageEvent
    // } else {
    //   messageData = (messageEvent as [string, unknown])[1]
    // }

    // const messageData  = messageEvent.data ? messageEvent.data : messageEvent
    const messageDataType = typeof messageData

    console.log('processSocketMessage messageDataType: ' + messageDataType)

    const socketMessage: SocketResultData<unknown> = messageData

    console.log('processSocketMessage socketMessage', socketMessage)

    // This is not really relevant anymore since socket listens to
    // messages for a specific comp.
    const socketListenConfig: SocketListenConfig = {
      listenForCompIds: [state.competitionOnTheDay.id],
      listenForMessageDomains: SOCKET_DOMAINS,
      listenForMessageTypes: [...RESULT_SOCKET_ACTION_TYPES]
    }

    const shouldAccept = SocketService.shouldSocketMessageBeAccepted(
      socketMessage,
      socketListenConfig
    )

    console.log('processSocketMessage shouldAccept', shouldAccept)

    if (shouldAccept.should) {
      console.log('shouldAccept', shouldAccept)

      vibrateController.vibrate([300, 100, 300])

      // processSocketPayload(socketMessage.action, socketMessage.payload as SocketPayloadAccepted)
      processSocketPayload(socketMessage)
    } else {
      console.warn(
        'processSocketMessage() shouldAccept.should: ' +
          shouldAccept.should +
          ' - ' +
          shouldAccept.reason
      )
    }
  }

  /**
   *
   * @param socketResultData
   */
  function processSocketPayload(
    socketResultData: SocketResultData<unknown>
    // action: ResultSocketActionType,
    // payload: SocketPayloadAccepted,
    // liveFeedMessageServer: LiveFeedMessageServer | undefined = undefined
  ) {
    let liveFeedMessageBase: LiveFeedMessageBase | LiveFeedMessageBaseGeneric<unknown> | undefined =
      undefined

    // let eventNo: EventId | EventIdString = 0
    let isUserFollowingEvent = true
    const action = socketResultData.action
    const payload = socketResultData.payload

    // const messageId = liveFeedMessageServer ? liveFeedMessageServer.id : -1
    const messageId = -1

    const isGreaterThanCompAtSocketId =
      messageId === -1 ? true : messageId > state.socket.compAtSocketId
    console.log(
      'setUpLiveFeed() messageId: ' +
        messageId +
        ', server: ' +
        state.socket.compAtSocketId +
        ', isGreaterThanCompAtSocketId: ' +
        isGreaterThanCompAtSocketId +
        (isGreaterThanCompAtSocketId ? ' - OK' : ' - NO need to process')
    )

    if (action === 'cache-updated') {
      const payloadCacheUpdated = payload as CompetitionOnTheDay
      console.log('processSocketPayload() cache-updated', payloadCacheUpdated)
      // socketProcessCacheUpdated(payloadCacheUpdated)
    }

    if (action === 'athlete-bibno') {
      const payloadAthleteBibNo = payload as SocketResultMessagePayloadAthleteBibNo
      console.log('processSocketPayload() athlete-bibno', payloadAthleteBibNo)
      if (isGreaterThanCompAtSocketId) {
        socketProcessAthleteBibNo(payloadAthleteBibNo)
      }
    }

    if (action === 'field-nextup') {
      const payloadFieldNextup = payload as SocketResultMessagePayloadFieldNextup
      console.log('processSocketPayload() field-nextup', payloadFieldNextup)
      if (isGreaterThanCompAtSocketId) {
        socketProcessFieldNextup(payloadFieldNextup)
      }
    }

    //  process message
    if (action === 'results-manual') {
      const payloadResultsManual = payload as SocketResultMessagePayloadResultsManual
      console.log('processSocketPayload() results-manual', payloadResultsManual)
      if (isGreaterThanCompAtSocketId) {
        socketProcessResultsManual(payloadResultsManual)
      }

      isUserFollowingEvent = ResultsService.isUserFollowingEvent(
        state.socket.followingEvents,
        payloadResultsManual.eventGroup.id
      )
      console.log(
        'isUserFollowingEvent: ' +
          isUserFollowingEvent +
          ' eventGroupId: ' +
          payloadResultsManual.eventGroup.id +
          '-' +
          payloadResultsManual.eventGroup.name
      )
      liveFeedMessageBase = isUserFollowingEvent
        ? LiveFeedService.factoryLiveFeedMessageField(action, payloadResultsManual)
        : undefined
    }

    if (action === 'field-results') {
      const payloadFieldResults = payload as SocketResultMessagePayloadFieldResults
      console.log('processSocketPayload() field-results', payloadFieldResults)
      if (isGreaterThanCompAtSocketId) {
        socketProcessFieldResults(payloadFieldResults)
      }

      isUserFollowingEvent = ResultsService.isUserFollowingEvent(
        state.socket.followingEvents,
        payloadFieldResults.ranking.eventGroup.id
      )
      console.log(
        'isUserFollowingEvent: ' +
          isUserFollowingEvent +
          ' eventGroupId: ' +
          payloadFieldResults.ranking.eventGroup.id +
          '-' +
          payloadFieldResults.ranking.eventGroup.name
      )

      liveFeedMessageBase = isUserFollowingEvent
        ? LiveFeedService.factoryLiveFeedMessageField(action, payloadFieldResults)
        : undefined
    }

    if (action === 'photofinish') {
      const payloadPhotoFinish = payload as SocketsResultsPfPayload
      console.log('processSocketPayload() photofinish', payloadPhotoFinish)
      if (isGreaterThanCompAtSocketId) {
        socketProcessPhotoResults(payloadPhotoFinish)
      }

      isUserFollowingEvent = ResultsService.isUserFollowingEvent(
        state.socket.followingEvents,
        payloadPhotoFinish.eventGroup.id
      )
      console.log(
        'isUserFollowingEvent: ' +
          isUserFollowingEvent +
          ' eventGroupId: ' +
          payloadPhotoFinish.eventGroup.id +
          '-' +
          payloadPhotoFinish.eventGroup.name
      )

      liveFeedMessageBase = isUserFollowingEvent
        ? LiveFeedService.factoryLiveFeedMessageField(action, payloadPhotoFinish)
        : undefined
    }

    if (action === 'seed-dropped') {
      const payloadSeedDropped = payload as SocketResultMessagePayloadSeedDropped
      console.log('processSocketPayload() seed-dropped', payloadSeedDropped)
      socketProcessSeedDropped(payloadSeedDropped)
    }

    if (action === 'track-move') {
      const payloadTrackMove = payload as SocketResultMessagePayloadTrackMove
      console.log('processSocketPayload() track-move', payloadTrackMove)
      socketProcessTrackMove(payloadTrackMove)
    }

    if (action === 'track-movevert') {
      const payloadTrackMoveVert = payload as SocketResultMessagePayloadTrackMoveVert
      console.log('processSocketPayload() track-movevert', payloadTrackMoveVert)
      socketProcessTrackMoveVert(payloadTrackMoveVert)
    }

    if (action === 'track-movehorz' || action === 'track-movehoriz') {
      const payloadTrackMoveHorz = payload as SocketResultMessagePayloadTrackMoveHorz
      console.log('processSocketPayload() track-movehorz', payloadTrackMoveHorz)
      socketProcessTrackMoveHorz(payloadTrackMoveHorz)
    }

    if (action === 'entries-present') {
      const payloadEntriesPresent = payload as SocketResultMessagePayloadEntriesPresent
      console.log('processSocketPayload() entries-present', payloadEntriesPresent)
      socketProcessEntriesPresent(payloadEntriesPresent)
    }

    if (action === 'seed-confirmed') {
      const payloadSeedConfirmed = payload as SocketResultMessagePayloadSeedConfirmed
      console.log('processSocketPayload() seed-confirmed', payloadSeedConfirmed)
      socketProcessSeedConfirmed(payloadSeedConfirmed)
    }

    if (action === 'entries-checkin') {
      const payloadEntriesCheckin = payload as SocketResultMessagePayloadEntriesCheckin
      console.log('processSocketPayload() entries-checkin', payloadEntriesCheckin)
      socketProcessEntriesCheckin(payloadEntriesCheckin)
    }

    //  For now we are not processing qualify-results messages, but we will keep the code here for future reference.
    // if (action === 'qualify-results') {
    //   const payloadQualifyResults = payload as SocketResultMessagePayloadQualifyResults
    //   console.log('processSocketPayload() qualify-results', payloadQualifyResults)
    //   socketProcessQualifyResults(payloadQualifyResults)
    // }

    // TODO
    /*
    if (liveFeedMessageBase) {
      if (liveFeedMessageServer) {
        liveFeedMessageBase.displayTime = liveFeedMessageServer.created
      }
      // addToLiveFeed(liveFeedMessageBase)
    }
    */
    //  vue reactivity...if Loading from Live feed...doing this is unnecessary.
    // if (!liveFeedMessageServer) {
    //   setScheduleToDisplay()
    // }
    // addToLiveFeed(liveFeedMessageBase)

    // if One of the sockets we are interested in Adli feed socket messages

    const isValid = RESULT_SOCKET_ACTION_TYPES.includes(action as ResultSocketActionType)

    if (isValid) {
      // const socketResultData: SocketResultData<unknown> = {
      //   action: action,
      //   domain: '',
      //   comp: state.competitionOnTheDay,
      //   payload: payload,
      //   key: '',
      //   deviceKey: ''
      // }
      addToLiveFeedSocketMessages(socketResultData)
    }
  }

  /**
   *
   * @param liveFeedMessage
   */
  function addToLiveFeed(liveFeedMessage: LiveFeedMessageBase) {
    if (!liveFeedMessage.payload) {
      return
    }
    //  Put the new message at the beginning of the array and
    //  if the new array exceeds the liveFeedQueueSize then remove the last item
    state.socket.liveFeed.unshift(liveFeedMessage)
    if (state.socket.liveFeed.length > state.socket.liveFeedQueueSize) {
      state.socket.liveFeed.pop()
    }
  }

  /**
   *
   * @param socketResultData
   */
  function addToLiveFeedSocketMessages(socketResultData: SocketResultData<unknown>) {
    if (!socketResultData.payload) {
      return
    }

    // Use the robust deduplication service
    if (socketDeduplicationService.isDuplicate(socketResultData)) {
      return
    }

    // Add to deduplication service
    socketDeduplicationService.addMessage(socketResultData)

    // Also maintain backward compatibility with the old maps for any existing code that might reference them
    state.maps.socketsByKey[socketResultData.key] = socketResultData
    if (!state.maps.socketsByContentHash) {
      state.maps.socketsByContentHash = {}
    }
    // Note: We don't store in socketsByContentHash anymore as the service handles this more efficiently

    let compEvent: CompEvent | undefined = undefined

    // Find matching event by event group ID from the state
    if (socketResultData.action === 'field-nextup') {
      //  If the soccer result data dot payload dot athlete has no keys then we have no
      //  athlete data so we don't want it in the live feed
      const payloadFieldNextup = socketResultData.payload as SocketResultMessagePayloadFieldNextup
      if (!Object.keys(payloadFieldNextup.athlete).length) {
        return
      }

      const payloadResultsManual = socketResultData.payload as SocketResultMessagePayloadFieldNextup
      const eventGroupId = payloadResultsManual.event.id
      compEvent = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
        state.competitionOnTheDay,
        eventGroupId
      )
      if (!compEvent) {
        console.warn(
          'addToLiveFeedSocketMessages() No comp event found for event group id: ' + eventGroupId
        )
        return
      }
    }

    const liveFeedSocketMessage = LiveFeedService.convertSocketResultDataToLiveFeedMessage(
      socketResultData,
      compEvent
    )

    // return socketResultsData.map((socketResultData) => {
    //   return LiveFeedService.convertSocketResultDataToLiveFeedMessage(socketResultData)
    // })

    // set a guid
    // socketResultData.key = createGuid()

    //  Put the new message at the beginning of the array and
    //  if the new array exceeds the liveFeedQueueSize then remove the last item
    state.socket.liveFeedSocketMessages.unshift(liveFeedSocketMessage)
    if (state.socket.liveFeedSocketMessages.length > state.socket.liveFeedQueueSize) {
      state.socket.liveFeedSocketMessages.pop()
    }

    // The robust deduplication service now handles cleanup automatically
  }

  /**
   *
   * @param payload
   */
  function updateTrialResults(payload: SocketResultMessagePayloadFieldResults) {
    const trialResultsUpdated = SocketFieldResultsService.updateTrialResults(
      payload,
      state.competitionOnTheDay.trialResults
    )
    state.competitionOnTheDay.trialResults = trialResultsUpdated
  }

  /**
   *
   * @param payload
   */
  function socketProcessAthleteBibNo(payload: SocketResultMessagePayloadAthleteBibNo) {
    console.log('socketProcessAthleteBibNo()', payload)

    //  Update the bibNo on the athlete in state.athletes
    const athlete = state.athletes[payload.athleteId]
    if (athlete) {
      // athlete. = payload.bibNo
    }
  }

  /**
   *
   * @param payload
   */
  function socketProcessFieldNextup(payload: SocketResultMessagePayloadFieldNextup) {
    console.log('socketProcessFieldNextup()', payload)
  }

  /**
   *
   * @param socketResultMessagePayloadResultsManual
   */
  function socketProcessResultsManual(
    socketResultMessagePayloadResultsManual: SocketResultMessagePayloadResultsManual
  ) {
    const mappedResults = SocketResultsManualService.mapResultsManual(
      socketResultMessagePayloadResultsManual
    )

    updateCompEventResults(socketResultMessagePayloadResultsManual.eventGroup.id, mappedResults)
  }

  /**
   *
   * @param socketResultMessagePayloadFieldResults
   */
  function socketProcessFieldResults(
    socketResultMessagePayloadFieldResults: SocketResultMessagePayloadFieldResults
  ) {
    const athletes = LiveFeedService.getFieldResultsAthletes(socketResultMessagePayloadFieldResults)
    const athleteIds = Object.keys(athletes)

    const mappedResults = SocketFieldResultsService.mapSocketFieldResultsResultsToIndividualResults(
      socketResultMessagePayloadFieldResults,
      athleteIds.length === 1 ? Number(athleteIds[0]) : undefined,
      athleteIds.length === 1 ? Object.values(athletes)[0] : undefined
    )

    updateCompEventResults(
      socketResultMessagePayloadFieldResults.ranking.eventGroup.id,
      mappedResults
    )

    updateTrialResults(socketResultMessagePayloadFieldResults)
  }

  /**
   *
   * @param socketsResultsPfPayload
   */
  function socketProcessPhotoResults(socketsResultsPfPayload: SocketsResultsPfPayload) {
    // Convert socket message into an actual result model that we can store against the state.
    const mappedResults =
      SocketsResultsPfService.mapSocketTrackResultsToIndividualResults(socketsResultsPfPayload)

    updateCompEventResults(socketsResultsPfPayload.eventGroup.id, mappedResults)
  }

  /**
   *
   * @param compEvent
   */
  function updateCompetitionOnTheDayEvent(compEvent: CompEvent) {
    state.competitionOnTheDay.events[compEvent.egId.toString()] = compEvent
  }

  /**
   *
   * @param compEvent
   */
  function processUpdateSelectedEvent(compEvent: CompEvent) {
    if (state.selectedEvent.compEventFiltered.egId === compEvent.egId) {
      console.log('processUpdateSelectedEvent() Updating selected event', compEvent)
      // showDetails(compEvent)
      state.selectedEvent.compEvent = CommonServiceUtils.simpleClone(compEvent)
      state.selectedEvent.compEventFiltered = CommonServiceUtils.simpleClone(compEvent)

      searchTermChanged(state.selectedEvent.searchTerm)
    }
  }

  /**
   * qazzaq
   * @param payload
   */
  function socketProcessSeedDropped(payload: SocketResultMessagePayloadSeedDropped) {
    console.log('socketProcessSeedDropped()', payload)

    // get egID from payload from first entry
    const egId = payload[0].egId

    // Find start list and update the entry
    const compEvent: CompEvent | undefined = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
      state.competitionOnTheDay,
      egId
    )
    if (!compEvent) {
      console.warn('socketProcessSeedDropped() No comp event found for event group id: ' + egId)
      return
    }

    const compEventOnCompetitonOnTheDay = state.competitionOnTheDay.events[egId.toString()]
    if (!compEventOnCompetitonOnTheDay) {
      console.warn(
        'socketProcessSeedDropped() No comp event found for event group id: ' +
          egId +
          ' in state.competitionOnTheDay.events'
      )
      return
    }

    const compEventUpdated = updateSeedDropped(compEvent, payload)
    updateCompetitionOnTheDayEvent(compEventUpdated)
    processUpdateSelectedEvent(compEventUpdated)
  }

  /**
   *
   * @param payload
   */
  function socketProcessTrackMove(payload: SocketResultMessagePayloadTrackMove) {
    console.log('socketProcessTrackMove()', payload)

    // using entryId of A, get from maps the egId
    const egId = state.maps.entriesByEntryId[payload.entryA.entryId.toString()]
    console.log('socketProcessTrackMove() egId: ' + egId)
    if (!egId) {
      console.warn('socketProcessTrackMove() No egId found for entryId: ' + payload.entryA.entryId)
      return
    }

    // Find start list and update the entry
    const compEvent: CompEvent | undefined = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
      state.competitionOnTheDay,
      Number(egId)
    )
    if (!compEvent) {
      console.warn('socketProcessSeedDropped() No comp event found for event group id: ' + egId)
      return
    }

    const compEventUpdated = updateTrackMove(payload, compEvent)
    updateCompetitionOnTheDayEvent(compEventUpdated)
    processUpdateSelectedEvent(compEventUpdated)
  }

  /**
   *
   * @param payload
   */
  function socketProcessTrackMoveVert(payload: SocketResultMessagePayloadTrackMoveVert) {
    console.log('socketProcessTrackMoveVert()', payload)

    const compEvent: CompEvent | undefined = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
      state.competitionOnTheDay,
      payload.egId
    )
    if (!compEvent) {
      console.warn(
        'socketProcessSeedDropped() No comp event found for event group id: ' + payload.egId
      )
      return
    }

    const compEventUpdated = updateTrackMoveVert(compEvent, payload)
    updateCompetitionOnTheDayEvent(compEventUpdated)
    processUpdateSelectedEvent(compEventUpdated)
  }

  /**
   *
   * @param payload
   */
  function socketProcessTrackMoveHorz(payload: SocketResultMessagePayloadTrackMoveHorz) {
    console.log('socketProcessTrackMoveHorz()', payload)

    // get egId from payload from first entry
    const egId = state.maps.entriesByEntryId[payload.entryInfo[0].entryId.toString()]
    if (!egId) {
      console.warn(
        'socketProcessTrackMoveHorz() No egId found for entryId: ' + payload.entryInfo[0].entryId
      )
      return
    }

    // Find start list and update the entry
    const compEvent: CompEvent | undefined = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
      state.competitionOnTheDay,
      Number(egId)
    )
    if (!compEvent) {
      console.warn('socketProcessTrackMoveHorz() No comp event found for event group id: ' + egId)
      return
    }

    const compEventUpdated = updateTrackMoveHorz(compEvent, payload)
    updateCompetitionOnTheDayEvent(compEventUpdated)
    processUpdateSelectedEvent(compEventUpdated)
  }

  /**
   *
   * @param payload
   */
  function socketProcessEntriesPresent(payload: SocketResultMessagePayloadEntriesPresent) {
    console.log('socketProcessEntriesPresent()', payload)

    // get egId from payload
    let egId: string = payload.egId.toString()

    //  N.B!!!  If egId is 0, then we need to find the egId from the entryId
    if (!egId || egId === '0') {
      console.warn('socketProcessEntriesPresent() No egId found for entryId: ' + payload.entryIds)
      // get first entryIdm, then use state m,aps to get the egId
      let firstEntryId: string = payload.entryIds[0] as any as string
      if (typeof firstEntryId === 'number') {
        firstEntryId = (firstEntryId as number).toString()
      }

      const entriesByEntryId = state.maps.entriesByEntryId

      egId = entriesByEntryId[firstEntryId]
      if (!egId) {
        console.warn('socketProcessEntriesPresent() No egId found for entryId: ' + firstEntryId)
        return
      }
    }

    const searchEgId = Number(egId)

    // Find start list and update the entry
    const compEvent: CompEvent | undefined = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
      state.competitionOnTheDay,
      searchEgId
    )
    if (!compEvent) {
      console.warn('socketProcessEntriesPresent() No comp event found for event group id: ' + egId)
      return
    }

    const compEventUpdated = updateEntriesPresent(compEvent, payload)
    updateCompetitionOnTheDayEvent(compEventUpdated)
    processUpdateSelectedEvent(compEventUpdated)
  }

  /**
   *
   * @param payload
   */
  function socketProcessSeedConfirmed(payload: SocketResultMessagePayloadSeedConfirmed) {
    console.log('socketProcessSeedConfirmed()', payload)

    const egId = payload.egId

    // Find start list and update the entry
    const compEvent: CompEvent | undefined = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
      state.competitionOnTheDay,
      egId
    )
    if (!compEvent) {
      console.warn('socketProcessSeedConfirmed() No comp event found for event group id: ' + egId)
      return
    }
    const compEventUpdated = updateSeedConfirmed(compEvent, payload)
    updateCompetitionOnTheDayEvent(compEventUpdated)
    processUpdateSelectedEvent(compEventUpdated)
  }

  /**
   *
   * @param payload
   */
  function socketProcessEntriesCheckin(payload: SocketResultMessagePayloadEntriesCheckin) {
    console.log('socketProcessEntriesCheckin()', payload)
    console.log('socketProcessEntriesCheckin() collected', payload.collected)

    // get first entry id and get correspoinding egId
    const firstEntryId = Object.keys(payload.checkins)[0]
    const egId = state.maps.entriesByEntryId[firstEntryId]
    if (!egId) {
      console.warn('socketProcessEntriesCheckin() No egId found for entryId: ' + firstEntryId)
      return
    }

    // Find start list and update the entry
    const compEvent: CompEvent | undefined = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
      state.competitionOnTheDay,
      Number(egId)
    )
    if (!compEvent) {
      console.warn('socketProcessEntriesCheckin() No comp event found for event group id: ' + egId)
      return
    }

    const compEventUpdated = updateEntriesCheckIn(compEvent, payload)
    updateCompetitionOnTheDayEvent(compEventUpdated)
    processUpdateSelectedEvent(compEventUpdated)
  }

  function socketProcessQualifyResults(payload: SocketResultMessagePayloadQualifyResults) {
    console.log('socketProcessQualifyResults()', payload)

    const sourceEgId = payload.sourceEgId

    const compEvent: CompEvent | undefined = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
      state.competitionOnTheDay,
      sourceEgId
    )
    if (!compEvent) {
      console.warn(
        'socketProcessQualifyResults() No comp event found for event group id: ' + sourceEgId
      )
      return
    }

    const compEventClone = simpleClone(compEvent)
    applyQualifyResults(sourceEgId, payload.entries, compEventClone)
    updateCompetitionOnTheDayEvent(compEventClone)
    processUpdateSelectedEvent(compEventClone)
  }

  /**
   *
   * @param eventGroupId
   * @param results
   * @param athleteId     If a Field result, we will get a map of athletes, it should have one entry
   *                      but if the card loses socket, it can send multiple.  we are ONLY interested
   *                      if ONE, then we can animate the result.
   */
  function updateCompEventResults(
    eventGroupId: number,
    results: Record<HeatNoString, IndividualResult[]>
  ) {
    const sourceCompEvent = ResultsService.getCompetitionOnTheDayCompEventFromEgId(
      state.competitionOnTheDay,
      eventGroupId
    )
    if (!sourceCompEvent) {
      console.warn(
        'updateCompEventResults() EXITING No comp event found for event group id: ' + eventGroupId
      )
      return
    }

    const resultsKeys = Object.keys(results)
    console.log('updateCompEventResults() resultsKeys', resultsKeys)
    if (resultsKeys.length === 0) {
      console.warn('updateCompEventResults() EXITING No results found')
      return
    }

    if (!sourceCompEvent.results) {
      sourceCompEvent.results = {}
    }

    const resultsSource: Record<HeatNoString, IndividualResult[]> = CommonServiceUtils.simpleClone(
      sourceCompEvent.results
    )

    //  loop through the results and update the source results
    for (const heatNo of resultsKeys) {
      resultsSource[heatNo] = results[heatNo]
    }

    sourceCompEvent.results = resultsSource
    //  if selected comp event is the same as the source comp event then update the selected event
    if (state.selectedEvent.compEvent.egId === sourceCompEvent.egId) {
      console.log('socketProcessFieldResults() Updating selected event')
      state.selectedEvent.compEvent = CommonServiceUtils.simpleClone(sourceCompEvent)

      // TODO ...better to just update what in filtered.. but this gets it done for now.
      state.selectedEvent.compEventFiltered = CommonServiceUtils.simpleClone(sourceCompEvent)
      searchTermChanged(state.selectedEvent.searchTerm)
    }
  }

  /**
   *
   * @param compEvent
   * @param showTab
   */
  function showDetails(
    compEvent: CompEvent,
    showTab?: 'DETAIL_ENTRIES' | 'DETAIL_START_LIST' | 'DETAIL_RESULTS'
  ) {
    state.selectedEvent.compEvent = CommonServiceUtils.simpleClone(compEvent)
    state.selectedEvent.compEventFiltered = CommonServiceUtils.simpleClone(compEvent)

    //  On selecting event, should search term be cleared???
    // state.selectedEvent.searchTerm = ''
    searchTermChanged(state.selectedEvent.searchTerm)

    state.selectedEvent.eventDateDisplay = ResultsService.getCompEventDateDisplay(compEvent)
    state.selectedEvent.eventTimeDisplay = ResultsService.getCompEventStartTime(compEvent)
    if (compEvent.entries && Object.keys(compEvent.entries).length > 0) {
      state.selectedEvent.eventAgeGroup =
        compEvent.entries[Object.keys(compEvent.entries)[0]].eventAgeGroup
    }

    state.selectedEvent.hasEntries = !!(
      compEvent.entries && Object.keys(compEvent.entries).length > 0
    )
    state.selectedEvent.hasResults = ResultsService.compEventHasHowManyResultHeats(compEvent) > 0
    state.selectedEvent.isTrack = ResultsService.isTrackEvent(compEvent)
    state.selectedEvent.isTeam = ResultsService.isTeamEvent(compEvent)
    state.ui.showSection = 'EVENT_DETAIL'

    const forward = {
      egId: 0,
      name: ''
    }

    // has qualifyToEg
    if (compEvent.egOptions && compEvent.egOptions.seed && compEvent.egOptions.seed.qualifyToEg) {
      const qualifyToEg = compEvent.egOptions.seed.qualifyToEg

      if (typeof qualifyToEg.id === 'number' && qualifyToEg.id > 0) {
        console.warn('showDetails() qualifyToEg.id === 0')
        forward.egId = qualifyToEg.id
        // state.selectedEvent.qualify.forward.name = qualifyToEg.eventNo
        //   ? qualifyToEg.eventNo.toString()
        //   : qualifyToEg.name!
        forward.name = qualifyToEg.eventNo.toString()
      }

      // state.selectedEvent.compEvent.qualify.forward.egId = qualifyToEg.id
      // state.selectedEvent.compEvent.qualify.forward.name = qualifyToEg.name
    }

    state.selectedEvent.qualify.forward = forward

    if (showTab) {
      state.ui.eventDetail.showTab =
        showTab === 'DETAIL_RESULTS' && !state.selectedEvent.hasResults
          ? 'DETAIL_ENTRIES'
          : showTab
    } else {
      state.ui.eventDetail.showTab = state.selectedEvent.hasResults
        ? 'DETAIL_RESULTS'
        : 'DETAIL_ENTRIES'
    }

    // if (showTab !== 'DETAIL_ENTRIES') {
    //   // forcing to another specific tab
    //   state.ui.eventDetail.showTab = showTab
    // } else {
    //   state.ui.eventDetail.showTab = state.selectedEvent.hasResults
    //     ? 'DETAIL_RESULTS'
    //     : 'DETAIL_ENTRIES'
    // }

    //  If user has scrolled down a long way down schedule then we need to scroll back to the top.
    window.scrollTo(0, 0)
  }

  /**
   *
   * @param searchTerm
   */
  function searchTermChanged(searchTerm: string) {
    console.log('StoreCompResults.searchTermChanged() searchTerm: ' + searchTerm)
    state.selectedEvent.searchTerm = searchTerm
    //  We need to filter the entries if there is a search term, but has to be separate copy.
    if (state.selectedEvent.searchTerm.length > 0) {
      let compEventFiltered = ResultsService.filterEntries(
        state.selectedEvent.compEvent,
        state.selectedEvent.searchTerm
      )

      compEventFiltered = ResultsService.filterResults(
        compEventFiltered,
        state.selectedEvent.searchTerm
      )

      state.selectedEvent.compEventFiltered = compEventFiltered
    } else {
      state.selectedEvent.compEventFiltered = CommonServiceUtils.simpleClone(
        state.selectedEvent.compEvent
      )
    }
  }

  /**
   *
   */
  const getSelectedCompEventTitle = computed(() => {
    return state.selectedEvent.compEvent.typeNo + ': ' + state.selectedEvent.compEvent.name
  })

  /**
   * Destroy function - only cleans up timeouts, socket remains for reuse
   */
  function destroy() {
    // Clear both timeouts
    if (pointerForSocketKeepAlive !== null) {
      clearTimeout(pointerForSocketKeepAlive)
      pointerForSocketKeepAlive = null
    }

    if (pointerForCompetitionRefresh !== null) {
      clearTimeout(pointerForCompetitionRefresh)
      pointerForCompetitionRefresh = null
    }

    // Note: Socket is kept alive for reuse across components
    // Use destroySocket() if you need to completely clean up the socket
  }

  /**
   * Complete socket cleanup - use sparingly, only when truly needed
   */
  function destroySocket() {
    destroy() // Clear timeouts first
    cleanupSocket() // Then cleanup the socket
  }

  /**
   * Get socket connection information for debugging
   */
  function getSocketInfo() {
    return {
      socketId: socket?.id || 'Not connected',
      connected: socket?.connected || false,
      disconnected: socket?.disconnected || true,
      url: SOCKET_URL_AWS,
      handlersSetup: socket?._e4sHandlersSetup || false,
      globalSocketExists: !!globalSocket,
      socketInitialized: socketInitialized
    }
  }

  /**
   *
   * @param shouldFollowEvent
   */
  function shouldFollowEvent(shouldFollowEvent: ShouldFollowEvent) {
    const result = ResultsService.updateCurrentlyFollowing(
      state.socket.followingEvents,
      shouldFollowEvent
    )
    state.socket.followingEvents = CommonServiceUtils.simpleClone(result)
  }

  /**
   *
   */
  const followingEventCount = computed<number>(() => {
    if (!state.socket.followingEvents) {
      return 0
    }
    return Object.keys(state.socket.followingEvents).length
  })

  /**
   *
   * @param showFollowEventSetup
   */
  function setShowFollowEventSetup(showFollowEventSetup: boolean) {
    state.ui.showFollowEventSetup = showFollowEventSetup
  }

  /**
   *
   * @param eventIndex
   * @param showTab
   */
  function gotoEventIndex(
    eventIndex: number,
    showTab: 'DETAIL_ENTRIES' | 'DETAIL_START_LIST' | 'DETAIL_RESULTS' = 'DETAIL_ENTRIES'
  ) {
    console.log('gotoEVentIndex() eventIndex: ' + eventIndex)
    const compEvent = state.scheduledEvents[eventIndex]
    showDetails(compEvent, showTab)
  }

  /**
   *
   * @param egId
   */
  function getIndexOfEvent(egId: EventGroupId) {
    return state.scheduledEvents.findIndex((event) => event.egId === egId)
  }

  /**
   *
   */
  function gotoPreviousEvent(nextType?: 'T' | 'F' | '') {
    console.log('gotoPreviousEvent()')

    // what is index of current event in scheduledEvents?
    // const currentIndex = state.scheduledEvents.findIndex(
    //   (event) => event.egId === state.selectedEvent.compEvent.egId
    // )

    const currentIndex = getIndexOfEvent(state.selectedEvent.compEvent.egId)
    console.log('gotoPreviousEvent() currentIndex:  ' + currentIndex)

    //  We don't know how this shoudl work, for now just go to the next event.
    nextType = ''

    // If nextType is empty or undefined, proceed as currently does
    if (!nextType) {
      // get previous index
      const nextIndex = currentIndex - 1
      console.log('gotoPreviousEvent() nextIndex:  ' + nextIndex)
      if (nextIndex < 0) {
        return
      }
      gotoEventIndex(nextIndex)
      return
    }

    // Find the previous event that matches the specified type
    for (let i = currentIndex - 1; i >= 0; i--) {
      const event = state.scheduledEvents[i]
      const isTrack = event.typeNo[0] === 'T'

      // If nextType is 'T', find previous track event
      // If nextType is 'F', find previous event that is not a track event (field event)
      if ((nextType === 'T' && isTrack) || (nextType === 'F' && !isTrack)) {
        console.log('gotoPreviousEvent() found matching event at index:  ' + i)
        gotoEventIndex(i)
        return
      }
    }

    console.log('gotoPreviousEvent() no matching event found for type:  ' + nextType)
  }

  /**
   *
   */
  function gotoNextEvent(nextType?: 'T' | 'F' | '') {
    console.log('gotoNextEvent()')
    const currentIndex = getIndexOfEvent(state.selectedEvent.compEvent.egId)
    console.log('gotoNextEvent() currentIndex:  ' + currentIndex)

    //  We don't know how this shoudl work, for now just go to the next event.
    nextType = ''

    // If nextType is empty or undefined, proceed as currently does
    if (!nextType) {
      // get next index
      const nextIndex = currentIndex + 1
      console.log('gotoNextEvent() nextIndex:  ' + nextIndex)
      if (nextIndex >= state.scheduledEvents.length) {
        return
      }
      gotoEventIndex(nextIndex)
      return
    }

    // Find the next event that matches the specified type
    for (let i = currentIndex + 1; i < state.scheduledEvents.length; i++) {
      const event = state.scheduledEvents[i]
      const isTrack = event.typeNo[0] === 'T'

      // If nextType is 'T', find next track event
      // If nextType is 'F', find next event that is not a track event (field event)
      if ((nextType === 'T' && isTrack) || (nextType === 'F' && !isTrack)) {
        console.log('gotoNextEvent() found matching event at index:  ' + i)
        gotoEventIndex(i)
        return
      }
    }

    console.log('gotoNextEvent() no matching event found for type:  ' + nextType)
  }

  /**
   *
   * @param eventIndex
   * @param showTab
   */
  function gotoEvent(
    compEvent: CompEvent,
    showTab: 'DETAIL_ENTRIES' | 'DETAIL_START_LIST' | 'DETAIL_RESULTS' = 'DETAIL_ENTRIES'
  ) {
    console.log('gotoEvent() compEvent: ' + compEvent.typeNo + ': ' + compEvent.name, compEvent)

    // We need to check the compEvent is in the scheduledEvents, so switch date filter if not.
    const datePart = CommonServiceUtils.getDatePartFromIsoString(compEvent.eventDate)
    if (datePart !== state.schedule.filterDate) {
      filterScheduleDate(datePart)
    }

    showDetails(compEvent, showTab)
  }

  /**
   *
   * @param egId
   */
  function gotoQualifyingEvent(egId: EventGroupId | EventGroupIdString) {
    console.log('gotoQualifyingEvent() egId: ' + egId)
    const index = getIndexOfEvent(typeof egId === 'number' ? egId : Number(egId))
    const compEvent = state.competitionOnTheDay.events[egId.toString()]
    gotoEvent(compEvent, state.ui.eventDetail.showTab)
  }

  const getQualifyFrom = computed(() => {
    const egId = state.selectedEvent.compEvent.egId.toString()
    if (!state.maps.qualifyLinks[egId]) {
      return null
    }
    return state.maps.qualifyLinks[egId].from
  })

  const getQualifyTo = computed(() => {
    const egId = state.selectedEvent.compEvent.egId.toString()
    if (!state.maps.qualifyLinks[egId]) {
      return null
    }
    return state.maps.qualifyLinks[egId].to
  })

  /**
   *
   */
  const isSelectedEventFirst = computed(() => {
    return getIndexOfEvent(state.selectedEvent.compEvent.egId) === 0
  })

  /**
   *
   */
  const isSelectedEventLast = computed(() => {
    return getIndexOfEvent(state.selectedEvent.compEvent.egId) === state.scheduledEvents.length - 1
  })

  // is selected event a track event
  const isSelectedEventTrack = computed(() => {
    return ResultsService.isTrackEvent(state.selectedEvent.compEvent)
  })

  /**
   * A computed property that determines whether the currently selected event has eligibility (EG) rules defined.
   *
   * @variable {boolean} doesSelectedEventHaveEgRules
   * @description Evaluates if the selected event includes eligibility rules by checking if either 'auto'
   * or 'nonAuto' rules are defined in the event's
   * egOptions configuration. Returns true if any of these rules are present and not zero 'false'.
   */
  const doesSelectedEventHaveEgRules = computed(() => {
    const rules = state.selectedEvent.compEvent.egOptions?.seed?.qualifyToEg?.rules
    return !!(rules?.auto || rules?.nonAuto)
  })

  const getSelectedEventQualifyRules = computed(() => {
    if (!state.selectedEvent.compEvent.egOptions?.seed?.qualifyToEg?.rules) {
      return ''
    }

    return eventQualifyDescription(state.selectedEvent.compEvent.egOptions.seed.qualifyToEg.rules)
  })

  // does selected event have trials
  const doesSelectedEventHaveTrials = computed(() => {
    return doesCompEventHaveTrials(state.selectedEvent.compEvent)
  })

  const getSelectedEventTrialsDescription = computed(() => {
    if (!state.selectedEvent.compEvent.egOptions.trials) {
      return ''
    }
    return eventQualificationTrialsDescription(state.selectedEvent.compEvent.egOptions.trials)
  })

  const getSelectedEventAgeGroup = computed(() => {
    //  If compevent has entries, loop through first 10, if eventAgeGroup is not empty, and
    //  all are the same, then return that value, else return empty string
    const entries = state.selectedEvent.compEvent.entries
    if (entries) {
      const entryIds = Object.keys(entries)
      let eventAgeGroup = ''
      let count = 0
      for (const entryId of entryIds) {
        const entry = entries[entryId]
        if (entry.eventAgeGroup) {
          if (eventAgeGroup === '') {
            eventAgeGroup = entry.eventAgeGroup
          } else if (eventAgeGroup !== entry.eventAgeGroup) {
            return ''
          }
          count++
          if (count === 10) {
            break
          }
        }
      }
      if (count > 0) {
        return eventAgeGroup
      }
    }

    return state.selectedEvent.eventAgeGroup
  })

  /**
   * N.B. this is time the competition data was last updated on the SERVER!
   * The server caches the competition data and sends it to clients.  This is different to
   * the time the client last received the data, e.g. the state.longPollDateTime.
   */
  const getCompetitionCachedTime = computed(() => {
    if (state.competitionOnTheDay && state.competitionOnTheDay.cachedDataSummary) {
      // return CommonServiceUtils.getTimePartFromIsoString(
      //   state.competitionOnTheDay.cachedDataSummary.cachedAt
      // )

      const isoString = state.competitionOnTheDay.cachedDataSummary.cachedAt

      let timePart

      // if does not contain a T, then return empty string
      if (isoString.indexOf('T') === -1) {
        return ''
      }

      if (isUtcTime(isoString)) {
        // Create date object - this automatically converts to local time
        const dateObj = new Date(isoString)

        // Format with built-in methods that respect local timezone including DST
        timePart = dateObj.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        })
      } else {
        timePart = isoString.split('T')[1].split('.')[0]

        // take first 5 characters
        timePart = timePart.substring(0, 5)
      }

      if (timePart === 'Invalid Date') {
        return ''
      }
      if (timePart === '00:00') {
        return 'TBC'
      }
      return timePart
    }

    return 'NA'
  })

  /**
   * Get deduplication service statistics for monitoring
   */
  function getDeduplicationStats() {
    return socketDeduplicationService.getStats()
  }

  /**
   * Clear deduplication cache (useful for testing or troubleshooting)
   */
  function clearDeduplicationCache() {
    socketDeduplicationService.clear()
    console.log('Deduplication cache cleared')
  }

  return {
    state,
    vibrateController,
    wakeLock,
    isWakeLockOnText,
    isCompetitionOver: computed(() => {
      // const events = Object.values(state.competitionOnTheDay.events)
      // if (events.length === 0) return false

      // const lastEventDate = events.reduce((latest, event) => {
      //   const eventDate = new Date(event.eventDate)
      //   return eventDate > latest ? eventDate : latest
      // }, new Date(0))

      // const now = new Date()
      // return lastEventDate < now
      return false
    }),
    showStartListTab: computed(() => {
      return state.competitionOnTheDay.options?.cardInfo?.enabled ? true : false
    }),
    wakeLockDebug,
    getSelectedCompEventTitle,
    debugMessages,
    followingEventCount,
    isSelectedEventFirst,
    isSelectedEventLast,
    isSelectedEventTrack,
    doesSelectedEventHaveEgRules,
    getSelectedEventQualifyRules,
    doesSelectedEventHaveTrials,
    getSelectedEventTrialsDescription,
    getCompetitionCachedTime,
    getSelectedEventAgeGroup,
    webSocket: socket,

    getQualifyFrom,
    getQualifyTo,

    init,
    getCompetitionOnTheDay,
    filterSchedule,
    filterScheduleDate,
    clearFilterSchedule,
    wakeLockCToggle,
    showDetails,
    destroy,
    destroySocket,
    testSocketMessage,
    shouldFollowEvent,
    setEventTypeFilter,
    setShowFilters,
    setShowFollowEventSetup,
    searchTermChanged,
    gotoPreviousEvent,
    gotoNextEvent,
    processSocketMessage,
    gotoQualifyingEvent,
    initSetupCompetition,
    forceSocketReconnection,
    checkSocketHealth,
    smartSocketReconnection,
    getSocketInfo,
    getDeduplicationStats,
    clearDeduplicationCache
  }
}
